/**
 * Aba "Conferência": cada item que o site mostra, com a hora do dado, o atraso e
 * a fonte. É a prova de que a coluna está entregando antes de passá-la ao plugin
 * e depois da virada.
 */
import { useState } from 'react';

import { Button, StatusDot } from '@/components/atoms';
import { useAgora } from '@/hooks/useAgora';
import { useConferencia } from '@/hooks/usePortal';
import { formatarDataHora } from '@/i18n/formato';
import { COLUNAS, type ColunaId, type PortalSiteDetalhe } from '@/types/portal';
import {
  descreverAtrasoMin,
  descreverIdade,
  estadoDoItem,
  rotuloDaColuna,
  segundosDesde,
} from '@/utils/portal';

import styles from './Portal.module.css';

const FORMATO_HORA: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
};

const TEXTO_ESTADO = { ok: 'Em dia', warn: 'Atrasado', crit: 'Sem dado' } as const;
const CLASSE_ESTADO = {
  ok: styles.estadoOk,
  warn: styles.estadoWarn,
  crit: styles.estadoCrit,
} as const;

export function AbaConferencia({ detalhe }: { detalhe: PortalSiteDetalhe }) {
  const { data, isLoading, isError, isFetching, refetch } = useConferencia(detalhe.site_id);
  const agora = useAgora();
  const [filtro, setFiltro] = useState<ColunaId | 'todas'>('todas');

  const itens = (data?.itens ?? []).filter((i) => filtro === 'todas' || i.coluna === filtro);
  const contagem = { ok: 0, warn: 0, crit: 0 };
  for (const i of itens) contagem[estadoDoItem(i)] += 1;

  return (
    <section className={styles.cartao} aria-label="Conferência">
      <div className={styles.cartaoCabeca}>
        <div>
          <h3 className={styles.cartaoTitulo}>Conferência</h3>
          <p className={styles.cartaoResumo}>
            Hora do dado, atraso e fonte de cada item das colunas ativas. Atualiza sozinha a cada
            minuto.
          </p>
        </div>
        <div className={styles.cartaoEtiquetas}>
          <label className={styles.rotulo} htmlFor="filtro-conferencia">
            Coluna
          </label>
          <select
            id="filtro-conferencia"
            className={styles.entrada}
            value={filtro}
            onChange={(e) => setFiltro(e.target.value as ColunaId | 'todas')}
          >
            <option value="todas">Todas</option>
            {COLUNAS.map((c) => (
              <option key={c} value={c}>
                {rotuloDaColuna(c)}
              </option>
            ))}
          </select>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            loading={isFetching}
            onClick={() => void refetch()}
          >
            Atualizar
          </Button>
        </div>
      </div>
      <div className={styles.cartaoCorpo}>
        {isLoading && <p className={styles.mudo}>Conferindo…</p>}
        {isError && (
          <div role="alert" className={styles.avisoCrit}>
            Não foi possível montar a conferência agora.
          </div>
        )}
        {data && (
          <p className={styles.mudo} role="status">
            {itens.length} {itens.length === 1 ? 'item' : 'itens'}: {contagem.ok} em dia,{' '}
            {contagem.warn} atrasados, {contagem.crit} sem dado. Gerada{' '}
            {descreverIdade(segundosDesde(data.gerada_em, agora))}.
          </p>
        )}
        {data && itens.length === 0 && (
          <p className={styles.vazio}>
            Nenhum item para conferir. Ligue uma coluna na aba Colunas.
          </p>
        )}
        {itens.length > 0 && (
          <div className={styles.rolagem}>
            <table className={styles.tabela}>
              <thead>
                <tr>
                  <th scope="col">Estado</th>
                  <th scope="col">Coluna</th>
                  <th scope="col">Item</th>
                  <th scope="col">Hora do dado</th>
                  <th scope="col">Atraso</th>
                  <th scope="col">Fonte</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((i) => {
                  const e = estadoDoItem(i);
                  return (
                    <tr key={i.id}>
                      <td>
                        <span className={CLASSE_ESTADO[e]}>
                          <StatusDot state={e} label={TEXTO_ESTADO[e]} />
                          {TEXTO_ESTADO[e]}
                        </span>
                      </td>
                      <td>{rotuloDaColuna(i.coluna)}</td>
                      <td>
                        {i.rotulo}
                        {i.motivo && <div className={styles.linhaMeta}>{i.motivo}</div>}
                      </td>
                      <td className={styles.num}>
                        {i.as_of ? formatarDataHora(i.as_of, FORMATO_HORA) : '—'}
                      </td>
                      <td className={styles.num}>{descreverAtrasoMin(i.atraso_min)}</td>
                      <td>{i.fonte ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
